import { Link, Navigate, useLocation } from 'react-router-dom';

interface ListedState { code?: string; id?: string; title?: string }

/** Shown right after a seller submits a listing. Direct visits (no submit state)
 *  bounce to the dashboard rather than showing a stale "submitted" screen. */
export default function Listed() {
  const { state } = useLocation() as { state: ListedState | null };
  if (!state?.code && !state?.id) return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-[640px] mx-auto">
      <section className="section-dark relative overflow-hidden rounded-luxe bg-ink text-cream p-8 md:p-10 text-center">
        <div className="absolute inset-0 dotgrid-light opacity-20 bg-[length:18px_18px]" />
        <div className="relative">
          <div className="w-16 h-16 mx-auto rounded-full bg-sage/20 grid place-items-center mb-4">
            <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="#8FB89E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7" />
            </svg>
          </div>
          <div className="stamp text-gold text-[10px]">LISTING SUBMITTED · UNDER REVIEW</div>
          <h1 className="display text-3xl md:text-4xl mt-2 leading-tight">Your item is in for review</h1>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 border border-gold/30 px-3 py-1 text-[11px] font-semibold text-gold">
              ⏳ Reviewed within 24-48 hours
            </span>
            {state?.code && (
              <span className="inline-flex items-center gap-2 bg-cream/10 rounded-full px-3 py-1">
                <span className="stamp text-cream/60 text-[9px]">CODE</span>
                <span className="mono text-gold text-sm">{state.code}</span>
              </span>
            )}
          </div>
          <p className="text-cream/70 text-sm mt-4 max-w-[46ch] mx-auto leading-relaxed">
            Our team checks your photos, the serial/authenticity and that everything's in order - usually within 24-48 hours. It stays hidden from buyers until it's approved, then it goes live automatically and you'll be notified.
          </p>
        </div>
      </section>

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {[
          { icon: '🛡️', t: 'Serial & authenticity', b: 'Checked first, before anything else.' },
          { icon: '✅', t: 'Goes live', b: 'Approved items get listed.' },
          { icon: '💸', t: 'You get paid', b: 'Full price, on collection.' },
        ].map((s) => (
          <div key={s.t} className="card p-4 text-center">
            <div className="text-xl">{s.icon}</div>
            <div className="text-sm font-medium mt-1.5">{s.t}</div>
            <div className="text-[11px] text-mute mt-0.5 leading-snug">{s.b}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <Link to="/dashboard/sell" className="btn btn-outline flex-1 text-center">List another item</Link>
        <Link to="/dashboard" className="btn btn-primary flex-1 text-center">Back to dashboard</Link>
      </div>
    </div>
  );
}
